/**
 * Helpers do pagamento online (rota unificada /online-payments do backend).
 *
 * O cardápio não escolhe o provedor (InfinitePay, ...): o backend usa a integração
 * ativa da empresa do token. O front só exibe o status devolvido pela API e
 * NUNCA marca o pedido como pago por conta própria.
 */

// Estados da tentativa (OnlinePaymentDTO.status)
export const ONLINE_PAYMENT_STATUS = {
  CREATING: "CREATING",
  PENDING: "PENDING",
  UNKNOWN: "UNKNOWN",
  CREATION_FAILED: "CREATION_FAILED",
  CONFIRMED: "CONFIRMED",
  REVIEW: "REVIEW",
};

// Estados em que o cliente pode pedir uma nova tentativa (newAttempt: true)
export const RETRYABLE_STATUSES = [
  ONLINE_PAYMENT_STATUS.UNKNOWN,
  ONLINE_PAYMENT_STATUS.CREATION_FAILED,
];

// Estados finais: param o polling da página de retorno
export const FINAL_STATUSES = [
  ONLINE_PAYMENT_STATUS.CONFIRMED,
  ONLINE_PAYMENT_STATUS.REVIEW,
  ...RETRYABLE_STATUSES,
];

// Último pagamento online iniciado ({ paymentId, orderCode }). O prefixo "pedido."
// faz a chave ser limpa junto com o pedido ao trocar de empresa (config/company.js).
export const ONLINE_PAYMENT_STORAGE_KEY = "pedido.pagamento";

/**
 * Meios de pagamento que podem ser pagos online (Pix e cartões).
 * Usa a chave (payment_key) padrão do back e, como reserva, o nome exibido.
 */
export const isOnlinePayableMethod = (paymentKey, paymentName) => {
  const key = String(paymentKey || "").toLowerCase();
  const name = String(paymentName || "").toLowerCase();
  return (
    key === "pix" ||
    key.startsWith("cartao") ||
    name.includes("pix") ||
    name.includes("cartão") ||
    name.includes("cartao")
  );
};

/** Chave para o header Idempotency-Key (8–100 caracteres: letras, números, '.', '_', ':' ou '-'). */
export const newIdempotencyKey = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `pay-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

export const saveLastOnlinePayment = (payment) => {
  if (!payment?.paymentId) return;
  localStorage.setItem(
    ONLINE_PAYMENT_STORAGE_KEY,
    JSON.stringify({
      paymentId: payment.paymentId,
      orderCode: payment.orderCode,
    })
  );
};

export const getLastOnlinePayment = () => {
  try {
    const raw = localStorage.getItem(ONLINE_PAYMENT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

/**
 * Lê os parâmetros da URL de retorno: paymentId (acrescentado pelo back) e os
 * enviados pela InfinitePay (order_nsu, transaction_nsu, slug, receipt_url, capture_method).
 */
export const parseReturnParams = (search) => {
  const params = new URLSearchParams(search || "");
  const pick = (...names) => {
    for (const n of names) {
      const v = params.get(n);
      if (v) return v;
    }
    return undefined;
  };
  return {
    paymentId: pick("paymentId", "payment_id"),
    providerParams: {
      order_nsu: pick("order_nsu"),
      transaction_nsu: pick("transaction_nsu", "transaction_id"),
      slug: pick("slug", "invoice_slug"),
      receipt_url: pick("receipt_url"),
      capture_method: pick("capture_method"),
    },
  };
};
